import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Stack,
  Grid2,
  CircularProgress,
  Autocomplete,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import CommonDatePicker from '../datepicker/CommonDatePicker';
import config from "../common/Config";
import "../common/commonStyles.css";
import * as XLSX from 'xlsx';
import { encryptPayload, normalizeEncryptedApiResponse } from "../../utils/customerCrypto";
import { getCompanySettings, resolveCustomerApiEncrypt } from "../../utils/companySettingsCache";

const CrossOffReport = () => {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const [dates, setDates] = useState([startOfMonth, endOfMonth]);
  const [reportrange, setReportrange] = useState('');
  const [reportAmount, setReportAmount] = useState('');
  const [pageCndDataOptions, setPageCndDataOptions] = useState([]);
  const [pageCndData, setPageCndData] = useState('product_wise');
  const [productReport, setProductReport] = useState([]);
  const [showpickListOption, setShowpickListOption] = useState(false);
  const [pickListOptions, setPickListOptions] = useState([]);
  const [pickListWise, setPickListWise] = useState('');
  const [showSalesRepOption, setShowSalesRepOption] = useState(false);
  const [salesRepOptions, setSalesRepOptions] = useState([]);
  const [salesRepWise, setSalesRepWise] = useState('');
  const [footerTotals, setFooterTotals] = useState({
    totalqty: '0',
    missing_total: '0.00',
    total_cost: '0.00',
    total_price: '0.00',
    total_purchase_box_qty: '0'
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedPickList, setSelectedPickList] = useState(null);
  const [selectedSalesRep, setSelectedSalesRep] = useState(null);
  const accessToken = localStorage.getItem('accessToken');
  const token = localStorage.getItem('token');
  const userId = localStorage.getItem('userId');
  const handleDateChange = (value) => {
    setDates(value);
    if (value && value[0] && value[1]) {
      let startDate = format(value[0], 'dd-MM-yyyy');
      let endDate = format(value[1], 'dd-MM-yyyy');
      setReportrange(startDate + ' - ' + endDate);
      setPage(0);
    }
  };

  const handleChangeOption = (event) => {
    const value = event.target.value;
    setPageCndData(value);
    setPage(0);
  };

  const handlePickListChange = (event, newValue) => {
    setSelectedPickList(newValue);
    setPickListWise(newValue ? newValue.value : '');
    setPage(0);
  };

  const handleSalesRepChange = (event, newValue) => {
    setSelectedSalesRep(newValue);
    setSalesRepWise(newValue ? newValue.value : '');
    setPage(0);
  };

  const searchFormData = () => {
    fetchProductWiseData();
  };

  useEffect(() => {
    if (reportrange) {
      fetchPickListOptions();
      fetchSalesRepOptions();
    }
  }, [reportrange]);

  const fetchPickListOptions = async () => {
    if (!dates[0] || !dates[1]) return;
    let startDate = format(dates[0], 'dd/MM/yyyy');
    let endDate = format(dates[1], 'dd/MM/yyyy');
    let pdtrange = startDate + ' - ' + endDate;
    const pdtData = new FormData();
    pdtData.append('reportrange', pdtrange);

    const apiPickListUrl = `${config.API_URL}/getpicklist`;
    try {
      const response = await fetch(apiPickListUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: pdtData,
      });

      const data = await response.json();
      const formattedOptions = (data.picklist || []).map(item => ({
        label: `${item.picklist_number}`,
        value: item.picklist_number,
      }));
      setPickListOptions(formattedOptions);
    } catch (error) {
    }
  };

  const fetchSalesRepOptions = async () => {
    if (!dates[0] || !dates[1]) return;
    let startDate = format(dates[0], 'dd/MM/yyyy');
    let endDate = format(dates[1], 'dd/MM/yyyy');
    let pdtrange = startDate + ' - ' + endDate;
    const pdtData = new FormData();
    pdtData.append('reportrange', pdtrange);

    const apiPickListUrl = `${config.API_URL}/getSalesRep`;
    try {
      const settings = await getCompanySettings();
      const useEncrypt = resolveCustomerApiEncrypt(settings);
      const payload = { reportrange: pdtrange };
      let body = pdtData;
      if (useEncrypt) {
        const ep = encryptPayload(payload);
        if (ep) {
          body = new FormData();
          body.append('encrypted_payload', ep);
        }
      }
      const response = await fetch(apiPickListUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        body,
      });

      const raw = await response.json();
      const data = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;
      const formattedOptions = (data.data || []).map(item => ({
        label: `${item.first_name} ${item.last_name}`,
        value: item.id,
      }));
      setSalesRepOptions(formattedOptions);
    } catch (error) {
    }
  };

  const fetchProductWiseData = useCallback(async () => {
    if (!reportrange) return;

    setLoading(true);
    const settings = await getCompanySettings();
    const useEncrypt = resolveCustomerApiEncrypt(settings);
    const pdtData = new FormData();
    let ptype = pageCndData || 'product_wise';

    if (ptype === 'order_wise') {
      setPickListWise('');
      setSalesRepWise('');
      setSelectedPickList(null);
      setSelectedSalesRep(null);
    } else if (ptype === 'pick_list_wise') {
      setSalesRepWise('');
      setSelectedSalesRep(null);
    } else if (ptype === 'sales_rep_wise') {
      setPickListWise('');
      setSelectedPickList(null);
    }

    const plain = {
      draw: String(page + 1),
      start: String(page * rowsPerPage),
      length: String(rowsPerPage),
      reportrange,
      type: ptype,
      product_id: '',
      pick_list: pickListWise,
      sales_rep_id: salesRepWise,
    };
    if (useEncrypt) {
      const ep = encryptPayload(plain);
      if (ep) {
        pdtData.append('encrypted_payload', ep);
      } else {
        Object.entries(plain).forEach(([k, v]) => pdtData.append(k, v ?? ''));
      }
    } else {
      Object.entries(plain).forEach(([k, v]) => pdtData.append(k, v ?? ''));
    }

    const apiMissingProductUrl = `${config.API_URL}/get_missing_order_report`;

    try {
      const response = await fetch(apiMissingProductUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: pdtData,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch Not Order Report');
      }
      const raw = await response.json();
      const data = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;
      data.type = ptype;
      setProductReport(data.data || []);
      setTotalCount(data.recordsTotal || data.recordsFiltered || (data.data || []).length);
      setReportAmount(data.order_total + ' | Missing Amt : ' + data.missing_total);

      setFooterTotals({
        totalqty: data.totalqty || '0',
        missing_total: data.missing_total || '0.00',
        total_cost: data.total_cost || '0.00',
        total_price: data.total_price || '0.00',
        total_purchase_box_qty: data.total_purchase_box_qty || '0'
      });
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [accessToken, page, rowsPerPage, reportrange, pageCndData, pickListWise, salesRepWise]);

  useEffect(() => {
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
    const options = [
      { value: 'product_wise', key: 'Product Wise' },
      { value: 'order_wise', key: 'Order Wise' },
      { value: 'pick_list_wise', key: 'Pick List Wise' },
      { value: 'sales_rep_wise', key: 'Sales Rep Wise' },
    ];

    setPageCndDataOptions(options);
    if (dates[0] && dates[1]) {
      let startDate = format(dates[0], 'dd-MM-yyyy');
      let endDate = format(dates[1], 'dd-MM-yyyy');
      setReportrange(startDate + ' - ' + endDate);
    }
  }, []);

  useEffect(() => {
    if (reportrange) {
      fetchProductWiseData();
    }
  }, [fetchProductWiseData]);

  useEffect(() => {
    setShowpickListOption(false);
    setShowSalesRepOption(false);

    switch (pageCndData) {
      case "pick_list_wise":
        setShowpickListOption(true);
        break;
      case "sales_rep_wise":
        setShowSalesRepOption(true);
        break;
      default:
        break;
    }
  }, [pageCndData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getHeadersForType = (type) => {
    switch (type) {
      case 'product_wise':
        return ['#', 'Product (Code)', 'Order Type', 'Qty', 'Missing Amt'];
      case 'order_wise':
        return ['#', 'Customers (A/c No)', 'Order Date', 'Order Id', 'Order Amt', 'Missing Qty', 'Missing Amt', 'Delivered Amt', 'Return Amt', 'Status'];
      case 'pick_list_wise':
        return ['#', 'Customers (A/c No)', 'Order Date', 'Order Id', 'Order Amt', 'Missing Qty', 'Missing Amt', 'Delivered Amt', 'Return Amt', 'Status'];
      case 'sales_rep_wise':
        return ['#', 'Customers (A/c No)', 'Order Date', 'Order Id', 'Order Amt', 'Missing Qty', 'Missing Amt', 'Delivered Amt', 'Return Amt', 'Status'];
      default:
        return [];
    }
  };

  const renderCellContent = (row, type, colIndex, rowIndex) => {
    const headers = getHeadersForType(type);
    const header = headers[colIndex];

    if (header === '#') {
      return page * rowsPerPage + rowIndex + 1;
    }

    switch (type) {
      case 'product_wise':
        switch (header) {
          case 'Product (Code)':
            return row.product_name || '';
          case 'Order Type':
            return row.order_type || '';
          case 'Qty':
            return row.total_items || '0';
          case 'Missing Amt':
            return parseFloat(row.missing_total || 0).toFixed(2);
          default:
            return '';
        }
      case 'order_wise':
      case 'pick_list_wise':
      case 'sales_rep_wise':
        switch (header) {
          case 'Customers (A/c No)':
            return row.customer || '';
          case 'Order Date':
            return row.date || '';
          case 'Order Id':
            return row.reference_no || '';
          case 'Order Amt':
            return parseFloat(row.order_total || 0).toFixed(2);
          case 'Missing Qty':
            return row.missing_qty || '0';
          case 'Missing Amt':
            return parseFloat(row.missing_total || 0).toFixed(2);
          case 'Delivered Amt':
            return parseFloat(row.deliverd_total || 0).toFixed(2);
          case 'Return Amt':
            return parseFloat(row.return_total || 0).toFixed(2);
          case 'Status':
            return row.sale_status || '';
          default:
            return '';
        }
      default:
        return '';
    }
  };

  const handleDownloadExcel = async () => {
    const settings = await getCompanySettings();
    const useEncrypt = resolveCustomerApiEncrypt(settings);
    const pdtData = new FormData();
    let ptype = pageCndData || 'product_wise';

    const plain = {
      draw: '1',
      start: '0',
      length: totalCount || 10000,
      reportrange,
      type: ptype,
      product_id: '',
      pick_list: pickListWise,
      sales_rep_id: salesRepWise,
    };
    if (useEncrypt) {
      const ep = encryptPayload(plain);
      if (ep) {
        pdtData.append('encrypted_payload', ep);
      } else {
        Object.entries(plain).forEach(([k, v]) => pdtData.append(k, v ?? ''));
      }
    } else {
      Object.entries(plain).forEach(([k, v]) => pdtData.append(k, v ?? ''));
    }

    const apiMissingProductUrl = `${config.API_URL}/get_missing_order_report`;

    try {
      const response = await fetch(apiMissingProductUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: pdtData,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }

      const raw = await response.json();
      const data = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;
      const exportData = (data.data || []).map((row, index) => {
        const type = ptype;
        if (type === 'product_wise') {
          return {
            '#': index + 1,
            'Product (Code)': row.product_name || '',
            'Order Type': row.order_type || '',
            'Qty': row.total_items || '0',
            'Missing Amt': parseFloat(row.missing_total || 0).toFixed(2),
          };
        } else {
          return {
            '#': index + 1,
            'Customers (A/c No)': row.customer || '',
            'Order Date': row.date || '',
            'Order Id': row.reference_no || '',
            'Order Amt': parseFloat(row.order_total || 0).toFixed(2),
            'Missing Qty': row.missing_qty || '0',
            'Missing Amt': parseFloat(row.missing_total || 0).toFixed(2),
            'Delivered Amt': parseFloat(row.deliverd_total || 0).toFixed(2),
            'Return Amt': parseFloat(row.return_total || 0).toFixed(2),
            'Status': row.sale_status || '',
          };
        }
      });

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
      XLSX.writeFile(wb, 'CrossOffReport.xlsx');
    } catch (error) {
    }
  };

  const currentType = pageCndData || 'product_wise';
  const headers = getHeadersForType(currentType);
  const numericHeaders = ['Qty', 'Missing Amt', 'Order Amt', 'Missing Qty', 'Delivered Amt', 'Return Amt'];

  const getHeaderSx = (header) => {
    if (header === '#') return { width: '60px', minWidth: '60px', textAlign: 'center' };
    if (header === 'Product (Code)') return { width: '250px', minWidth: '200px' };
    if (header === 'Order Type') return { width: '120px', minWidth: '100px' };
    if (header === 'Customers (A/c No)') return { width: '200px', minWidth: '180px' };
    if (header === 'Order Date') return { width: '110px', minWidth: '100px' };
    if (header === 'Order Id') return { width: '120px', minWidth: '100px' };
    if (header === 'Status') return { width: '100px', minWidth: '90px' };
    if (numericHeaders.includes(header)) return { width: '110px', minWidth: '100px', textAlign: 'right' };
    return {};
  };

  const getCellAlign = (header) => {
    if (numericHeaders.includes(header)) return 'right';
    if (header === '#') return 'center';
    return 'left';
  };

  return (
    <>
      <Box className="page-container">
          <Paper className="paper-style">
            <Box sx={{ px:2, py:1.2 }}>
              <Grid2 container spacing={1} display="flex" justifyContent="space-between" alignItems="center">
                <Grid2 size={{ xs: 12, md: 2}}>
                <Typography className="page-title">
                Cross Off Report
              </Typography>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md:3}}>
                  <CommonDatePicker
                    className="input-border"
                    value={dates}
                    onChange={handleDateChange}
              
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 6, md:2}} alignContent="end">
                  <Autocomplete
                    size="small"
                    options={pageCndDataOptions}
                    value={pageCndData}
                    onChange={handleChangeOption}
                    getOptionLabel={(option) => option.key || ''}
                    renderInput={(params) => (
                      <TextField {...params}
                      label="Type" 
                      placeholder="Select Type" 
                      className="text-field input-border"
                      />
                    )}
                  />

                </Grid2>

                {showpickListOption && (
                  <Grid2 size={{ xs: 12, sm: 6, md: 3 }} alignContent="end">
                    <Autocomplete
                      size="small"
                      options={pickListOptions}
                      value={selectedPickList}
                      onChange={handlePickListChange}
                      getOptionLabel={(option) => option.label || ''}
                      renderInput={(params) => (
                        <TextField className="input-border" {...params} label="Pick List" placeholder="Select Pick List" />
                      )}
                    />
                  </Grid2>
                )}

                {showSalesRepOption && (
                  <Grid2 size={{ xs: 12, sm: 6, md: 3 }} alignContent="end">
                    <Autocomplete
                      size="small"
                      options={salesRepOptions}
                      value={selectedSalesRep}
                      onChange={handleSalesRepChange}
                      getOptionLabel={(option) => option.label || ''}
                      renderInput={(params) => (
                        <TextField className="input-border" {...params} label="Sales Rep" placeholder="Select Sales Rep" />
                      )}
                    />
                  </Grid2>
                )}

                <Grid2 size={{ xs: 12, sm: 6, md:2}}>
                  <Stack direction="row" spacing={1} justifyContent="end">
                    <Button
                      variant="contained"
                      className="button-variant-contained"
                      onClick={searchFormData}
                      fullWidth
                    >
                      Submit
                    </Button>
                    <Button
                      variant="outlined"
                      className="button-variant-outlined"
                      onClick={handleDownloadExcel}
                      fullWidth
                    >
                      Export
                    </Button>
                  </Stack>
                </Grid2>
              </Grid2>
            </Box>


          {reportAmount && (
            <Box sx={{ mb:1, p: 1, backgroundColor: '#e8eef7', borderRadius: 1 ,mx: 2}}>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12px', color: '#212b36' }}>
                Order Amt : <b style={{ fontWeight: 500, fontSize: '12px' }}>{reportAmount}</b>
              </Typography>
            </Box>
          )}

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={40} />
            </Box>
          ) : (
            <TableContainer>
              <Table size="medium" className="table-cell-border" sx={{ tableLayout: "fixed" }}>
                <TableHead>
                  <TableRow className="table-cell-header">
                    {headers.map((header, index) => (
                      <TableCell
                        key={index}
                        align={getCellAlign(header)}
                        sx={getHeaderSx(header)}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {productReport.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={headers.length} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" sx={{ color: "#637381" }}>
                          No data available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    productReport.map((row, rowIndex) => (
                      <TableRow
                        key={rowIndex}
                        hover
                        className="table-cell-style"
                        sx={{
                          "&:last-child td": {
                            borderBottom: "none",
                          },
                        }}
                      >
                        {headers.map((header, colIndex) => (
                          <TableCell
                            key={colIndex}
                            align={getCellAlign(header)}
                            sx={{
                              borderBottomColor: "rgba(145, 158, 171, 0.16)",
                              ...(numericHeaders.includes(header) ? { fontWeight: 500 } : {}),
                            }}
                          >
                            {renderCellContent(row, currentType, colIndex, rowIndex)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
                {productReport.length > 0 && (
                  <TableFooter>
                    <TableRow
                      sx={{
                        "& .MuiTableCell-root": {
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          color: "#212b36",
                          py: 1,
                          backgroundColor: "#f5f7fa",
                          borderBottom: "none",
                        },
                      }}
                    >
                      {headers.map((header, colIndex) => {
                        if (currentType === 'product_wise') {
                          if (header === 'Order Type') {
                            return (
                              <TableCell key={colIndex}>
                                Total
                              </TableCell>
                            );
                          }
                          if (header === 'Qty') {
                            return (
                              <TableCell key={colIndex} align="right">
                                {footerTotals.totalqty}
                              </TableCell>
                            );
                          }
                          if (header === 'Missing Amt') {
                            return (
                              <TableCell key={colIndex} align="right">
                                {parseFloat(footerTotals.missing_total || 0).toFixed(2)}
                              </TableCell>
                            );
                          }
                          return <TableCell key={colIndex} />;
                        }
                        if (header === '#') {
                          return (
                            <TableCell key={colIndex} align="center">
                              Total
                            </TableCell>
                          );
                        }
                        if (header === 'Missing Qty') {
                          return (
                            <TableCell key={colIndex} align="right">
                              {footerTotals.totalqty}
                            </TableCell>
                          );
                        }
                        if (header === 'Missing Amt') {
                          return (
                            <TableCell key={colIndex} align="right">
                              {parseFloat(footerTotals.missing_total || 0).toFixed(2)}
                            </TableCell>
                          );
                        }
                        return <TableCell key={colIndex} />;
                      })}
                    </TableRow>
                  </TableFooter>
                )}
              </Table>
            </TableContainer>
          )}

          {!loading && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage="Rows per page:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to}${count !== -1 ? ` of ${count}` : ''}`}
                className="pagination-styles"
              />
            </Box>
          )}
          </Paper>
        </Box>
    </>
  );
}

export default CrossOffReport;
